import { storeToRefs } from "pinia";

import { constantize, singularize } from "shared/helpers/string";
import type {
  InsightsLandingPageItemTarget,
  InsightsLandingPageItemTargetClass,
  InsightsLandingPageItemTargetString,
} from "shared/modules/Insights/types";
import { Favorite } from "shared/resources";
import FavoriteService from "shared/services/api/FavoriteService";
import { useUserStore } from "shared/stores/user";

export function useFavorites() {
  const { currentUser } = storeToRefs(useUserStore());

  async function addFavorite<
    T extends InsightsLandingPageItemTargetClass =
      InsightsLandingPageItemTargetClass,
  >(item: T): Promise<T> {
    const favorite = new Favorite({
      targetId: item.id,
      targetType: constantize(singularize(item.resourceIdentifier.type)),
      userId: currentUser.value.id,
    });

    await favorite.save();

    Object.assign(item, { favorite });

    return item;
  }

  async function removeFavorite<
    T extends InsightsLandingPageItemTargetClass =
      InsightsLandingPageItemTargetClass,
  >(item: T): Promise<T> {
    await item.favorite.destroy();

    Object.assign(item, { favorite: undefined });

    return item;
  }

  function toggleFavorite<
    T extends InsightsLandingPageItemTargetClass =
      InsightsLandingPageItemTargetClass,
  >(item: T): Promise<T> {
    if (item.favorite) {
      return removeFavorite(item);
    }

    return addFavorite(item);
  }

  async function bulkAddFavorites(
    type: InsightsLandingPageItemTargetString,
    ids: number[]
  ): Promise<void> {
    const targets: InsightsLandingPageItemTarget[] = ids.map((id) => ({
      id,
      type,
    }));

    await FavoriteService.bulkAddFavorite(targets);
  }

  async function bulkRemoveFavorites(
    type: InsightsLandingPageItemTargetString,
    ids: number[]
  ): Promise<void> {
    const targets: InsightsLandingPageItemTarget[] = ids.map((id) => ({
      id,
      type,
    }));

    await FavoriteService.bulkRemoveFavorite(targets);
  }

  return {
    addFavorite,
    bulkAddFavorites,
    bulkRemoveFavorites,
    removeFavorite,
    toggleFavorite,
  };
}

export default useFavorites;
